import { Auth } from '../utilities/auth';
import { logger } from '../utilities/logger.ts';
import { getApiBaseUrl } from '../application/api.ts';
import { ReferenceMetadata } from '../application/components/chat-window/chat-window.element.ts';

export type WsIncoming = {
  status: 'success';
  chatMessage: string;
  chatMessageResponse: string;
  chatSessionId: string;
  dbxMlflowRunId: string;
  newChat: boolean;
  metadata?: ReferenceMetadata[];
};

export type WsErrorIncoming = {
  status: 'error';
  message: string;
  chatMessage: string;
  chatSessionId?: string;
};

type Deferred<T> = {
  resolve: (v: T) => void;
  reject: (e: unknown) => void;
  settled: boolean;
};

type PendingMessage = {
  question: string;
  deferred: Deferred<WsIncoming>;
};

const NEW_CHAT_KEY = '__NEW_CHAT__';

function getSessionKey(sessionId?: string | null): string {
  return sessionId ?? NEW_CHAT_KEY;
}

export async function buildWsUrl(): Promise<string> {
  const baseUrl = await getApiBaseUrl();
  baseUrl.hostname = baseUrl.hostname.replace('rest.', 'ws.');
  baseUrl.protocol = 'wss';
  baseUrl.searchParams.append('access_token', Auth.accessToken ?? '');
  return baseUrl.toString();
}

/**
 * Manages WebSocket connections for sending and receiving chat messages.
 * Handles connection management, message sending, and response handling, including reconnect logic.
 *
 * Key Features:
 * - Establishes and maintains a WebSocket connection.
 * - Sends chat messages and waits for responses.
 * - Supports reconnection with exponential backoff.
 * - Ensures only one in-flight message per chat session.
 * - Allows listeners to subscribe to incoming WebSocket messages.
 */
export class WebSocketClient {
  private socket: WebSocket | null = null;
  private connection: Promise<void> | null = null;
  private pendingMessagesBySession = new Map<string, PendingMessage[]>();

  private reconnecting = false;
  private reconnectAttempts = 0;
  private manuallyClosed = false;

  /**
   * Establishes a WebSocket connection. If already connected, returns the existing connection promise.
   * @returns A promise that resolves when the connection is established.
   */
  connect(): Promise<void> {
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
      return this.connection ?? Promise.resolve();
    }

    this.connection = (async () => {
      const url = await buildWsUrl();

      return new Promise<void>((resolve, reject) => {
        try {
          this.socket = new WebSocket(url);

          this.socket.onopen = () => {
            logger.info('[WS] connected');
            this.reconnectAttempts = 0;
            resolve();
          };

          this.socket.onmessage = (event) => {
            try {
              const data = JSON.parse(event.data);

              if (data.status === 'error') {
                if (!data.payload) {
                  logger.error('[WS] Received an error response for an unidentified chat session:', data.message);
                  return;
                }
                data.chatMessage = data.payload.chatMessage;
                data.chatSessionId = data.payload.chatSessionId;
                logger.error(
                  '[WS] Received an error response for chat session:',
                  data.chatSessionId,
                  'Error:',
                  data.message,
                );
              }

              // First try to match to pending request by the returned chatSessionId
              const responseKey = getSessionKey(data.chatSessionId || undefined);
              let matchedSession = this.handleMatchingPending(responseKey, { ...data, newChat: false });

              // if no pending request under that session (e.g. from new chat),
              // fallback to NEW_CHAT_KEY session (first message created the session).
              if (!matchedSession) {
                matchedSession = this.handleMatchingPending(NEW_CHAT_KEY, { ...data, newChat: true });
              }

              if (!matchedSession) {
                logger.warn(
                  '[WS] No matching pending entry for',
                  { chatSessionId: data.chatSessionId, chatMessage: data.chatMessage },
                  'pending keys:',
                  Array.from(this.pendingMessagesBySession.keys()),
                );
              }
            } catch (e) {
              logger.error('[WS] incoming message parse error', e);
            }
          };

          this.socket.onclose = (ev) => {
            logger.warn('[WS] closed', ev.code, ev.reason);
            this.socket = null;

            // Reject all pending promises
            this.pendingMessagesBySession.forEach((pendingMessages) => {
              pendingMessages.forEach((pendingMessage) => {
                if (!pendingMessage.deferred.settled) {
                  pendingMessage.deferred.settled = true;
                  pendingMessage.deferred.reject(new Error('[WS] Connection closed unexpectedly.'));
                }
              });
            });
            this.pendingMessagesBySession.clear();

            if (!this.manuallyClosed) {
              this.scheduleReconnect();
            }
          };

          this.socket.onerror = (err) => {
            logger.error('[WS] error', err);
            if (this.socket && this.socket.readyState === WebSocket.CONNECTING) {
              reject(new Error('[WS] Connection error occurred during the initial connection attempt.'));
            }
          };
        } catch (e) {
          reject(e);
        }
      });
    })();

    this.connection.catch(() => {
      this.connection = null; // Reset connection if the promise is rejected
    });

    return this.connection;
  }

  // Close WebSocket connection and prevent automatic reconnection
  close(): void {
    this.manuallyClosed = true;
    if (this.socket) this.socket.close();
    this.socket = null;
  }

  // Schedules a reconnection attempt with exponential backoff.
  private scheduleReconnect() {
    if (this.reconnecting || this.manuallyClosed) return;
    this.reconnecting = true;
    const backoff = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 15000); // up to 15s
    this.reconnectAttempts += 1;
    setTimeout(async () => {
      this.reconnecting = false;
      try {
        await this.connect();
      } catch (e) {
        logger.error('[WS] reconnect failed', e);
      }
    }, backoff);
  }

  /**
   * Sends a chat message via WebSocket and waits for the next response for the specified chat session.
   *
   * - Ensures only one in-flight message per chat session (including "new chat").
   * - Resolves the returned promise with the server's response when received.
   * - Rejects the promise if an error occurs during message sending or if the WebSocket connection fails.
   *
   * @param chatMessage - The message to send.
   * @param chatSessionId - The chat session ID (optional for new chats).
   * @returns A promise that resolves with the server's response.
   * @throws An error if there is already a pending message for the specified chat session.
   */
  async sendChatMessage(chatMessage: string, chatSessionId?: string | null): Promise<WsIncoming> {
    // Ensure the WebSocket connection is established before sending the message.
    await this.connect();
    const sessionKey = getSessionKey(chatSessionId);

    let resolveFn!: (value: WsIncoming) => void;
    let rejectFn!: (reason?: unknown) => void;
    // Will be resolved or rejected when the server responds.
    const responsePromise = new Promise<WsIncoming>((resolve, reject) => {
      resolveFn = resolve;
      rejectFn = reject;
    });
    const deferredResponse: Deferred<WsIncoming> = {
      resolve: resolveFn,
      reject: rejectFn,
      settled: false,
    };

    // Push a new pending entry for this (session, question) pair
    const list = this.pendingMessagesBySession.get(sessionKey) ?? [];
    list.push({
      question: chatMessage,
      deferred: deferredResponse,
    });
    this.pendingMessagesBySession.set(sessionKey, list);

    const payload = {
      action: 'send-message' as const,
      chatMessage,
      // Include the chatSessionId only if it is provided. New chats are identified by the absence of this field.
      ...(chatSessionId ? { chatSessionId } : {}),
    };

    try {
      this.socket!.send(JSON.stringify(payload));
    } catch (e) {
      this.removePendingEntry(sessionKey, chatMessage);
      throw e;
    }

    return responsePromise;
  }

  /**
   * Try to resolve/reject a pending request in a given session that
   * matches the incoming data.chatMessage (question asked). Returns true if match found and resolved/rejected.
   */
  private handleMatchingPending(sessionId: string, data: WsIncoming | WsErrorIncoming): boolean {
    const pendingMessages = this.pendingMessagesBySession.get(sessionId);
    if (!pendingMessages || pendingMessages.length === 0) return false;

    // Find the first request whose expectedChatMessage matches incoming chatMessage
    const matchingMessage = pendingMessages.findIndex((pendingMessage) => pendingMessage.question === data.chatMessage);

    if (matchingMessage === -1) return false;

    const [pendingRequest] = pendingMessages.splice(matchingMessage, 1);

    if (pendingMessages.length === 0) {
      this.pendingMessagesBySession.delete(sessionId);
    } else {
      this.pendingMessagesBySession.set(sessionId, pendingMessages);
    }

    if (!pendingRequest.deferred.settled) {
      pendingRequest.deferred.settled = true;
      if (data.status === 'error') {
        pendingRequest.deferred.reject(new Error(data.message));
      } else {
        logger.info('[WS] Response retrieved for ', {
          chatSessionId: data.chatSessionId,
          chatMessage: data.chatMessage,
        });
        pendingRequest.deferred.resolve(data);
      }
    }

    return true;
  }

  /**
   * Remove a single pending entry for (sessionKey, expectedChatMessage).
   */
  private removePendingEntry(sessionKey: string, expectedChatMessage: string): void {
    const pendingMessages = this.pendingMessagesBySession.get(sessionKey);
    if (!pendingMessages) return;
    const matchingMessage = pendingMessages.findIndex((e) => e.question === expectedChatMessage);
    if (matchingMessage !== -1) {
      pendingMessages.splice(matchingMessage, 1);
      if (pendingMessages.length === 0) this.pendingMessagesBySession.delete(sessionKey);
      else this.pendingMessagesBySession.set(sessionKey, pendingMessages);
    }
  }

  /**
   * Retrieves the most recently asked pending question for a given chat session.
   * @param chatSessionId - The chat session ID.
   * @returns The most recent pending question, or null if no pending questions exist.
   */
  getPendingQuestion(chatSessionId: string | null): string | null {
    const sessionKey = getSessionKey(chatSessionId);
    const pendingMessages = this.pendingMessagesBySession.get(sessionKey);
    if (!pendingMessages || pendingMessages.length === 0) {
      return null;
    }
    // Return the question of the last pending message
    return pendingMessages[pendingMessages.length - 1].question;
  }
}

export const wsClient = new WebSocketClient();
